import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Dialog, IconButton, LinearProgress, Popover, Stack, Tooltip, Typography, alpha, } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SmartDisplayRoundedIcon from '@mui/icons-material/SmartDisplayRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { getCachedEnvConfig, loadEnvConfig, readHelpVideoLinks } from '../services/envConfig';
const POPOVER_ID = 'video-help-widget-popover';
const ACCENT = '#E23744';
const WATCHED_STORAGE_KEY = 'onestop.helpVideos.watched';
function readWatchedSet() {
    try {
        const raw = window.localStorage.getItem(WATCHED_STORAGE_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(arr) ? arr.filter((v) => typeof v === 'string') : []);
    }
    catch {
        return new Set();
    }
}
function persistWatchedSet(set) {
    try {
        window.localStorage.setItem(WATCHED_STORAGE_KEY, JSON.stringify(Array.from(set)));
    }
    catch {
        /* private/blocked storage — watched state just won't persist across visits */
    }
}
/** Rewrites a Google Drive/YouTube share link into its embeddable player URL; passes anything else through. */
function toEmbedUrl(url) {
    const drive = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
    if (drive)
        return `https://drive.google.com/file/d/${drive[1]}/preview`;
    const ytWatch = url.match(/[?&]v=([^&]+)/);
    if (ytWatch && /youtube\.com/.test(url))
        return `https://www.youtube.com/embed/${ytWatch[1]}`;
    const ytShort = url.match(/youtu\.be\/([^?&]+)/);
    if (ytShort)
        return `https://www.youtube.com/embed/${ytShort[1]}`;
    return url;
}
/**
 * Floating launcher for `env.json` `HelpVideos` — a step-by-step onboarding list that
 * opens an inline preview player, tracks watched progress (persisted per-browser in
 * `localStorage`), and offers a one-click copy of each link. Styled and sized to match
 * `SupportWidgetButton` (56px circle, bottom-left corner) and placed one slot further
 * right (`left: 148`) so the two sit in the same row without overlapping — see
 * `SupportWidgetButton.tsx` for why that corner is used and how it relates to the
 * accessibility widget's own trigger.
 *
 * Renders nothing when `HelpVideos` is empty/unset, so an unconfigured portal doesn't
 * show a dead button.
 */
export function VideoHelpWidgetButton() {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const [videos, setVideos] = useState(() => readHelpVideoLinks(getCachedEnvConfig()));
    const [watched, setWatched] = useState(() => readWatchedSet());
    const [previewVideo, setPreviewVideo] = useState(null);
    const [copiedUrl, setCopiedUrl] = useState(null);
    useEffect(() => {
        void loadEnvConfig().then((env) => setVideos(readHelpVideoLinks(env ?? getCachedEnvConfig())));
    }, []);
    if (videos.length === 0)
        return null;
    const open = Boolean(anchorEl);
    const close = () => setAnchorEl(null);
    const watchedCount = videos.filter((v) => watched.has(v.url)).length;
    const progressPct = Math.round((watchedCount / videos.length) * 100);
    const markWatched = (url) => {
        if (watched.has(url))
            return;
        const next = new Set(watched);
        next.add(url);
        setWatched(next);
        persistWatchedSet(next);
    };
    const handleOpenPreview = (video) => {
        setPreviewVideo(video);
        markWatched(video.url);
    };
    const handleCopy = async (e, url) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(url);
            setCopiedUrl(url);
            window.setTimeout(() => setCopiedUrl((cur) => (cur === url ? null : cur)), 1500);
        }
        catch {
            /* clipboard unavailable — row's own href still works as a fallback */
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Tooltip, { title: "Help videos", placement: "right", children: _jsxs(Box, { component: "button", type: "button", onClick: (e) => setAnchorEl(e.currentTarget), "aria-label": "Watch help videos", "aria-haspopup": "dialog", "aria-expanded": open, "aria-controls": open ? POPOVER_ID : undefined, sx: {
                        position: 'fixed',
                        left: 148,
                        bottom: 20,
                        zIndex: 2147483000,
                        width: 56,
                        height: 56,
                        p: 0,
                        borderRadius: '28px',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(145deg, ${ACCENT} 0%, #B3212C 100%)`,
                        color: '#ffffff',
                        boxShadow: open ? `0 4px 16px ${alpha(ACCENT, 0.55)}` : `0 4px 14px ${alpha(ACCENT, 0.4)}`,
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 22px ${alpha(ACCENT, 0.5)}`,
                        },
                    }, children: [_jsx(SmartDisplayRoundedIcon, { sx: { fontSize: 28 } }), watchedCount === 0 && (_jsx(Box, { "aria-hidden": true, sx: {
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: '#FFC107',
                                border: `2px solid ${theme.palette.background.paper}`,
                            } }))] }) }), _jsxs(Popover, { id: POPOVER_ID, open: open, anchorEl: anchorEl, onClose: close, anchorOrigin: { vertical: 'top', horizontal: 'left' }, transformOrigin: { vertical: 'bottom', horizontal: 'left' }, marginThreshold: 12, transitionDuration: 180, slotProps: {
                    paper: {
                        role: 'dialog',
                        'aria-label': 'Help videos',
                        sx: {
                            mb: 1.5,
                            width: 360,
                            maxWidth: 'calc(100vw - 24px)',
                            maxHeight: 'calc(100vh - 96px)',
                            overflow: 'hidden',
                            borderRadius: 3,
                            boxShadow: '0 16px 40px rgba(0,0,0,0.24), 0 2px 8px rgba(0,0,0,0.12)',
                            border: `1px solid ${theme.palette.divider}`,
                        },
                    },
                }, children: [_jsxs(Stack, { sx: {
                            px: 2,
                            py: 1.75,
                            background: `linear-gradient(135deg, ${alpha(ACCENT, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1.5, children: [_jsx(Box, { sx: {
                                            width: 36,
                                            height: 36,
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            background: `linear-gradient(145deg, ${ACCENT} 0%, #B3212C 100%)`,
                                            color: '#fff',
                                            boxShadow: `0 3px 10px ${alpha(ACCENT, 0.4)}`,
                                        }, children: _jsx(SmartDisplayRoundedIcon, { sx: { fontSize: 20 } }) }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 700, lineHeight: 1.25 }, children: "Getting started" }), _jsxs(Typography, { variant: "caption", sx: { color: theme.palette.text.secondary }, children: [watchedCount, " of ", videos.length, " watched"] })] }), _jsx(IconButton, { size: "small", onClick: close, "aria-label": "Close help videos", sx: { color: theme.palette.text.secondary, flexShrink: 0 }, children: _jsx(CloseRoundedIcon, { fontSize: "small" }) })] }), _jsx(LinearProgress, { variant: "determinate", value: progressPct, sx: {
                                    mt: 1.25,
                                    height: 5,
                                    borderRadius: 3,
                                    bgcolor: alpha(ACCENT, 0.14),
                                    '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: ACCENT },
                                } })] }), _jsx(Stack, { sx: { p: 1, gap: 0.5, maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' }, children: videos.map((video, index) => {
                            const isWatched = watched.has(video.url);
                            const isCopied = copiedUrl === video.url;
                            return (_jsxs(Box, { role: "button", tabIndex: 0, onClick: () => handleOpenPreview(video), onKeyDown: (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleOpenPreview(video);
                                    }
                                }, sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    px: 1.25,
                                    py: 1,
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    color: theme.palette.text.primary,
                                    opacity: 0,
                                    animation: 'helpVideoRowIn 0.28s ease forwards',
                                    animationDelay: `${index * 45}ms`,
                                    transition: 'background-color 0.12s ease, transform 0.12s ease',
                                    '@keyframes helpVideoRowIn': {
                                        from: { opacity: 0, transform: 'translateY(4px)' },
                                        to: { opacity: 1, transform: 'translateY(0)' },
                                    },
                                    '&:hover': { backgroundColor: alpha(ACCENT, 0.08), transform: 'translateX(2px)' },
                                    '&:hover .video-copy-btn': { opacity: 1 },
                                    '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                                }, children: [_jsx(Box, { sx: {
                                            width: 30,
                                            height: 30,
                                            flexShrink: 0,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 13,
                                            fontWeight: 700,
                                            backgroundColor: isWatched ? alpha('#2E7D32', 0.14) : alpha(ACCENT, 0.12),
                                            color: isWatched ? '#2E7D32' : ACCENT,
                                            transition: 'background-color 0.15s ease, color 0.15s ease',
                                        }, children: isWatched ? _jsx(CheckRoundedIcon, { fontSize: "small" }) : index + 1 }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 500, wordBreak: 'break-word', lineHeight: 1.35 }, children: video.name }), isWatched && (_jsx(Typography, { variant: "caption", sx: { color: '#2E7D32', fontWeight: 600 }, children: "Watched" }))] }), _jsx(Tooltip, { title: isCopied ? 'Copied!' : 'Copy link', placement: "top", children: _jsx(IconButton, { size: "small", className: "video-copy-btn", onClick: (e) => void handleCopy(e, video.url), "aria-label": `Copy link for ${video.name}`, sx: {
                                                opacity: 0,
                                                transition: 'opacity 0.12s ease',
                                                color: isCopied ? '#2E7D32' : theme.palette.text.disabled,
                                                flexShrink: 0,
                                                '&:focus-visible': { opacity: 1 },
                                            }, children: _jsx(ContentCopyRoundedIcon, { sx: { fontSize: 16 } }) }) })] }, `${video.url}-${index}`));
                        }) })] }), _jsxs(Dialog, { open: Boolean(previewVideo), onClose: () => setPreviewVideo(null), maxWidth: "md", fullWidth: true, PaperProps: { sx: { borderRadius: 3, overflow: 'hidden' } }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1.5, sx: { px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}` }, children: [_jsx(SmartDisplayRoundedIcon, { sx: { color: ACCENT, fontSize: 20 } }), _jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 700, flex: 1, minWidth: 0 }, noWrap: true, children: previewVideo?.name }), previewVideo && (_jsx(Tooltip, { title: "Open in new tab", children: _jsx(IconButton, { size: "small", component: "a", href: previewVideo.url, target: "_blank", rel: "noopener noreferrer", "aria-label": "Open video in new tab", children: _jsx(OpenInNewRoundedIcon, { fontSize: "small" }) }) })), _jsx(IconButton, { size: "small", onClick: () => setPreviewVideo(null), "aria-label": "Close video preview", children: _jsx(CloseRoundedIcon, { fontSize: "small" }) })] }), _jsx(Box, { sx: { position: 'relative', width: '100%', pt: '56.25%', bgcolor: '#000' }, children: previewVideo && (_jsx(Box, { component: "iframe", src: toEmbedUrl(previewVideo.url), title: previewVideo.name, allow: "autoplay; encrypted-media; picture-in-picture", allowFullScreen: true, sx: { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' } })) })] })] }));
}
